import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import API_BASE_URL from "../../../../Constants";

const InvoiceForm = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // Capture the invoiceID from the URL
  const [buildings, setBuildings] = useState([]);
  const [formData, setFormData] = useState({
    invoiceNumber: "",
    invoiceDate: "",
    customer: "",
    buildingID: "",
    addressOne: "",
    addressTwo: "",
    city: "",
    state: "",
    zip: "",
    invoiceAmount: "",
    partsAmount: "",
    discountAmount: "",
    taxAmount: "",
    open: true, // or other default value
  });

  useEffect(() => {
    fetchBuildings();
    if (id) {
      fetchInvoice(id); // Fetch invoice data if invoiceID is provided
    }
  }, [id]);

  const fetchBuildings = useCallback(async () => {
    try {
      const response = await fetch(
        API_BASE_URL + `/api/setup/buildings/all`,
        {
          credentials: "include",
        }
      );
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const data = await response.json();
      const buildings = Object.values(data);
      setBuildings(buildings);
    } catch (error) {
      console.error("Fetch error: ", error);
      setBuildings([]);
    }
  }, []);

  const fetchInvoice = useCallback(async (id) => {
    try {
      const response = await fetch(API_BASE_URL + `/api/invoice/${id}`, {
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error("Failed to fetch invoice");
      }
      const invoiceData = await response.json();
      setFormData(invoiceData); // Populate form with invoice data
    } catch (error) {
      console.error("Fetch invoice error: ", error);
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, type, value, checked } = e.target;
    setFormData({ ...formData, [name]: type === "checkbox" ? checked : value });
  };

  const handleSave = async () => {
    if (id) {
      try {
        const response = await fetch(
          API_BASE_URL + `/api/invoice/${id}`,
          {
            method: "PUT",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(formData),
          }
        );
        if (response.ok) {
          alert("Invoice saved successfully");
          navigate("/invoicing/invoice-entry/:id");
        } else {
          throw new Error("Failed to save invoice");
        }
      } catch (error) {
        console.error("Error saving invoice:", error);
        alert("An error occurred while saving the invoice");
      }
    } else {
      alert("No invoice selected to save");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(
        API_BASE_URL + `/api/invoice/invoice-entry-page`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        }
      );
      if (response.ok) {
        alert("Invoice added successfully");
        setFormData({
          invoiceNumber: "",
          invoiceDate: "",
          customer: "",
          buildingID: "",
          addressOne: "",
          addressTwo: "",
          city: "",
          state: "",
          zip: "",
          invoiceAmount: "",
          partsAmount: "",
          discountAmount: "",
          taxAmount: "",
          open: true,
        });
        navigate("/invoicing/invoice-list/");
      } else {
        throw new Error("Failed to add invoice");
      }
    } catch (error) {
      console.error("Error adding invoice:", error);
      alert("An error occurred while adding the invoice");
    }
  };

  return (
    <div className="w-5/6 mx-auto mt-10 p-4">
      <h2 className="text-2xl font-bold mb-4">
        {id ? "Edit Invoice" : "Create Invoice"}
      </h2>
      <div className="grid grid-cols-1 gap-6">
        <form className="bg-white p-6 rounded-lg shadow-lg border">
          {/* Invoice Fields */}
          <div className="grid grid-cols-1 gap-4">
            <div className="flex items-center gap-4 mb-4">
              <div className="flex-1">
                <label className="block font-semibold">Invoice Number:</label>
                <input
                  type="text"
                  name="invoiceNumber"
                  value={formData.invoiceNumber}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded focus:outline-none focus:border-blue-500"
                />
              </div>
              <div className="flex flex-col justify-center">
                <label className="font-semibold mr-2">Open?</label>
                <input
                  type="checkbox"
                  name="open"
                  checked={formData.open}
                  onChange={handleInputChange}
                  className="form-checkbox h-5 w-5 text-blue-500 border-gray-300 rounded focus:ring focus:ring-blue-200 my-3"
                />
              </div>
            </div>
            <div>
              <label className="block font-semibold">Invoice Date:</label>
              <input
                type="date"
                name="invoiceDate"
                value={formData.invoiceDate}
                onChange={handleInputChange}
                className="w-full p-2 border rounded focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block font-semibold">Customer:</label>
              <input
                type="text"
                name="customer"
                value={formData.customer}
                onChange={handleInputChange}
                className="w-full p-2 border rounded focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block font-semibold">Building ID:</label>
              <select
                name="buildingID"
                value={formData.buildingID}
                onChange={handleInputChange}
                className="w-full p-2 border rounded focus:outline-none focus:border-blue-500"
              >
                <option value="">Select Building</option>
                {buildings.map((building) => (
                  <option key={building.id} value={building.id}>
                    {building.name ||
                      building.buildingName ||
                      building.buildingID}
                  </option>
                ))}
              </select>
            </div>
            {/* Address Fields */}
            <div>
              <label className="block font-semibold">Address One:</label>
              <input
                type="text"
                name="addressOne"
                value={formData.addressOne}
                onChange={handleInputChange}
                className="w-full p-2 border rounded focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block font-semibold">Address Two:</label>
              <input
                type="text"
                name="addressTwo"
                value={formData.addressTwo}
                onChange={handleInputChange}
                className="w-full p-2 border rounded focus:outline-none focus:border-blue-500"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block font-semibold">City:</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded focus:outline-none focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block font-semibold">State:</label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded focus:outline-none focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block font-semibold">ZIP Code:</label>
                <input
                  type="text"
                  name="zip"
                  value={formData.zip}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>
            {/* Financial Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block font-semibold">Invoice Amount:</label>
                <input
                  type="number"
                  name="invoiceAmount"
                  value={formData.invoiceAmount}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded focus:outline-none focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block font-semibold">Parts Amount:</label>
                <input
                  type="number"
                  name="partsAmount"
                  value={formData.partsAmount}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded focus:outline-none focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block font-semibold">Discount Amount:</label>
                <input
                  type="number"
                  name="discountAmount"
                  value={formData.discountAmount}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded focus:outline-none focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block font-semibold">Tax Amount:</label>
                <input
                  type="number"
                  name="taxAmount"
                  value={formData.taxAmount}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>
          </div>
          {/* Save and Cancel Buttons */}
          <div className="flex justify-between mt-6">
            <button
              type="button"
              onClick={handleSubmit}
              className="px-6 py-2 bg-yellow-500 text-white font-bold rounded-lg hover:bg-yellow-600 transition"
            >
              Submit
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="px-6 py-2 bg-yellow-500 text-white font-bold rounded-lg hover:bg-yellow-600 transition"
            >
              Save
            </button>
            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              className="px-6 py-2 border border-gray-500 text-gray-500 rounded-lg hover:bg-gray-200 transition"
            >
              Cancel
            </button>
          </div>
        </form>
    
      </div>
    </div>
  );
};

export default InvoiceForm;